import { forwardRef, ChangeEvent, ReactNode, ReactElement, Ref, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { AppBar, Backdrop, makeStyles, useTheme, useMediaQuery, Slide, Tabs, Toolbar, Grid as MuiGrid } from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { adviserProductRoles, supportRoles } from '../../common/types';
import { useAppDispatch, useAppSelector } from '../../store';
import { clearClient, clearMessage } from '../../store/adviser';
import { clearCalculateBalances } from '../../store/calculate';
import { getNccAsync } from '../../store/clientApplication';
import AdviserMenu from '../adviser/AdviserMenu';
import ConfirmSave from '../adviser/disclosureTemplate/ConfirmSave';
import Grid, { Banner, Body, Header } from '../adviser/Grid';
import ToolsMenu from '../adviser/tools/ToolsMenu';
import { AlertMessage, Colors, DialogBox, LoadingAnimation, SubtitleText, Tab, TitleText } from '../controls/helpers';
import { getRoles } from '../adviser/Helpers';

let timeout: number | undefined = undefined;

const screens = [
  { route: 'select-products' },
  { route: 'application-details' },
  { route: 'kiwisaver' },
  { route: 'onboarding-status' },
  { route: 'disclosure-template', isTool: true },
  { route: 'kiwisaver-calculator', isTool: true },
  { route: 'kiwisaver-retention-tool', isTool: true },
  { route: 'savings-advice-tool', isTool: true }
];

const lastPathSegment = (path: string) => {
  const segments = path.split('/');
  return segments[segments.length - 1];
}

const aspMssRoles = ['ASP', 'ASP Support', 'MSS', 'MSS Support'];

const AdviserScreen = ({ children, history, isChanged, location }: RouteComponentProps & { children: ReactNode; isChanged?: boolean; }) => {
  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const currentRoute = lastPathSegment(location.pathname);
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const currentScreen = screens.find(({ route }) => route === currentRoute);
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isSupportRole, setIsSupportRole] = useState<boolean | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);
  const [chosenTabValue, setChosenTabValue] = useState<number>(0);
  const { user } = useAppSelector(state => state.oidc);
  const [hasAspMssRoles, setHasAspMssRoles] = useState<boolean>(false);
  const [hasKsRetentionRole, setHasKsRetentionRole] = useState(false);
  const [toolsMenuAnchor, setToolsMenuAnchor] = useState<HTMLElement | undefined>(undefined);
  const { isLoading, isSubmitting, message } = useAppSelector(state => state.adviser);
  const { isLoading: isLoadingNcc, nccList } = useAppSelector(state => state.clientApplication);

  const handleTabChange = (event: ChangeEvent<{}>, newValue: any) => {
    event.preventDefault();
    if (newValue !== 3) {
      setTabValue(newValue);
      setChosenTabValue(newValue);
      window.clearTimeout(timeout);
      dispatch(clearClient());
      dispatch(clearCalculateBalances());
      dispatch(clearMessage());
      if (newValue === 1) hasChanged ? setIsMounted(true) : history.push('/adviser/new-application/select-products', location.state);
      else if (newValue === 2) hasChanged ? setIsMounted(true) : history.push('/adviser/onboarding-status');
    }
  }

  useEffect(() => {
    dispatch(getNccAsync());
  }, []);

  useEffect(() => {
    if (!user) return;
    const roles = getRoles(user);
    setIsSupportRole(!roles.some(role => adviserProductRoles.includes(role)) && roles.some(role => supportRoles.includes(role)));
    setHasAspMssRoles(roles.some(role => aspMssRoles.includes(role)));
    setHasKsRetentionRole(roles.includes('KiwiSaver Retain Search'));
  }, [user]);

  useEffect(() => {
    if (currentScreen?.route === 'onboarding-status') setTabValue(2);
    else if (currentScreen?.isTool) setTabValue(3);
    else setTabValue(1);
    setHasChanged(isChanged!);
  }, [currentScreen, hasChanged, isSupportRole]);

  useEffect(() => {
    window.clearTimeout(timeout);
    if (!!message) timeout = window.setTimeout(() => { dispatch(clearMessage()); }, 6000);
  }, [message]);

  return <Grid>
    <Backdrop style={{ zIndex: 9999 }} open={isLoadingNcc || !nccList || isLoading || isSubmitting}>
      <LoadingAnimation />
    </Backdrop>
    <Banner className={classes.banner}>
      <AppBar position="static">
        <Toolbar disableGutters className={classes.toolbar}>
          <MuiGrid container>
            <MuiGrid item xs={4} style={{ padding: '12px 20px 12px' }}>
              <SubtitleText hideMarginBottom>
                <span className={classes.username}>{user?.profile.preferred_username}</span>
              </SubtitleText>
            </MuiGrid>
            <MuiGrid item xs={4} className={classes.centerAlign}>
              <TitleText hideMarginBottom className={classes.portLink}>
                <a href={process.env.REACT_APP_PORT_URL}>PORT</a>
              </TitleText>
            </MuiGrid>
            <MuiGrid item xs={4} className={classes.rightMenu}>
              <AdviserMenu />
            </MuiGrid>
          </MuiGrid>
        </Toolbar>
      </AppBar>
    </Banner>
    <Header>
      {currentRoute !== 'client-application' && currentRoute !== 'advice-confirmation' &&
        <AppBar elevation={0} position="sticky" className={classes.appBar}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth" className={classes.tabs}>
            <Tab disabled disableRipple className={classes.spacerTab} />
            <Tab label="New application" disableRipple />
            <Tab label="Onboarding status" disableRipple />
            {(hasAspMssRoles || !isSupportRole) &&
              <Tab
                classes={{ labelIcon: classes.labelIcon }}
                label="Tools"
                disableRipple
                icon={<ExpandMoreIcon style={{ paddingLeft: 5, paddingTop: 2, transition: 'transform 200ms', transform: toolsMenuAnchor ? 'rotate(-180deg) translateX(-5px) translateY(-2px)' : '' }} />}
                onClick={e => setToolsMenuAnchor(!toolsMenuAnchor ? e.currentTarget : undefined)}
              />
            }
          </Tabs>
        </AppBar>
      }
    </Header>
    {!!message &&
      <Header>
        <AlertMessage message={message.text} error={message.error} />
      </Header>
    }
    <Body className={classes.body}>
      <ToolsMenu
        anchor={toolsMenuAnchor}
        close={() => setToolsMenuAnchor(undefined)}
        menuItems={[
          ...!isSupportRole ? [{ label: 'Disclosure template', route: '/adviser/disclosure-template' }] : [],
          ...!isSupportRole ? [{ label: 'KiwiSaver calculator', route: '/adviser/kiwisaver-calculator' }] : [],
          ...!isSupportRole && hasKsRetentionRole ? [{ label: 'KiwiSaver retention tool', route: '/adviser/kiwisaver-retention-tool' }] : [],
          ...hasAspMssRoles ? [{ label: 'New client agreement', href: `${process.env.REACT_APP_ADVISER_URL}/fee-structure/new?nocache` }] : [],
          ...!isSupportRole ? [{ label: 'New risk profile', href: 'https://portal.nzfunds.co.nz/RiskLaunch/' }] : [],
          ...hasAspMssRoles ? [{ label: 'New risk rating', href: `${process.env.REACT_APP_ADVISER_URL}/risk-rating/new?nocache` }] : [],
          ...!isSupportRole ? [{ label: 'Savings Advice Tool', route: '/adviser/savings-advice-tool' }] : []
        ]}
      />
      {children}
    </Body>
    <DialogBox
      disableBackdropClick
      disableEscapeKeyDown
      open={isMounted}
      onClose={() => { setHasChanged(false); setIsMounted(false); }}
      TransitionComponent={Transition}
      fullScreen={fullScreen}>
      <ConfirmSave
        Yes={() => chosenTabValue == 0 ? history.push('/adviser/new-application/select-products', location.state) : history.push('/adviser/onboarding-status')}
        No={() => { setHasChanged(false); setIsMounted(false); }}
      />
    </DialogBox>
  </Grid>
}

const Transition = forwardRef(function Transition(props: TransitionProps & { children?: ReactElement }, ref: Ref<unknown>) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles({
  appBar: {
    backgroundColor: Colors.LightGrey,
    color: Colors.Black,
    width: '100%',
    marginTop: 35
  },
  body: {
    padding: '0 !important',
    position: 'relative'
  },
  banner: {
    position: 'relative',
    '& .MuiAppBar-root': {
      backgroundColor: Colors.Black,
      color: Colors.White,
      boxShadow: 'unset'
    }
  },
  labelIcon: {
    paddingTop: 6
  },
  portLink: {
    left: 0,
    right: 0,
    bottom: 2,
    textAlign: 'center',
    color: `${Colors.White} !important`,
    '& a': {
      color: 'inherit',
      textDecoration: 'none'
    },
    flexGrow: 1,
    fontSize: '1.875rem'
  },
  centerAlign: {
    textAlign: 'center'
  },
  rightMenu: {
    textAlign: 'right',
    paddingRight: 12,
    paddingTop: 6
  },
  username: {
    color: Colors.White,
    textTransform: 'uppercase'
  },
  toolbar: {
    minHeight: 50,
    height: 50
  },
  tabs: {
    '& .MuiTabs-indicator': {
      position: 'absolute',
      height: '100%',
      borderTop: `1px solid ${Colors.Grey}`,
      borderLeft: `1px solid ${Colors.Grey}`,
      borderRight: `1px solid ${Colors.Grey}`,
      zIndex: 1
    },
    '& .MuiTabs-flexContainer': {
      borderBottom: `1px solid ${Colors.Grey}`
    }
  },
  spacerTab: {
    minWidth: 60,
    '@media(max-width: 768px)': {
      display: 'none'
    }
  }
}, { name: 'AdviserScreen' });

export default withRouter(AdviserScreen);
